import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import { parseGeoCSV } from "../../data/geoOptions";
import { searchGeos, fetchGeosByKeys, validateZipCode, parseZipCSV, buildZipGeoObject } from "../../services/geoService";
import { getCurrentMonthFirstDay } from "../../utils/calendarHelpers";
import { AUDIO_TYPES as BUDGET_DRAWER_ALL_AUDIO } from "../../data/audioTypes";
import { PRICING_MODES, DEFAULT_PRICING_MODE } from "../../constants/ui";
import { notifyChange } from "../notifications/notify";
import { resumeCampaignsIfEligible, RESUME_LOOKUP_FAILED_MESSAGE } from "../../services/advertiserActivation";
import { capShortfall, sanitizeCpaInput } from "./budgetMath";
import { ShortfallWarning } from "./ShortfallWarning";

const DEFAULT_GEO = { id:"US", key:"US", name:"United States", display_name:"United States", type:"country", clusivity:"include" };

function toTitleCase(str) {
  return str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Formats a campaign-settings snapshot as markdown for the CSM notification.
// Pricing Mode is intentionally omitted: only admins can change it, admin
// saves are gated client-side, so previous_state and new_state from a real
// notification will always carry the same mode. The $ / % unit on the CPA
// value already implies the mode. Geos and Audio Types degrade gracefully
// on shape drift (objects with display_name → name → key string).
function formatCampaignSettingsMarkdown(snapshot) {
  if (!snapshot) return "";
  const isPct = snapshot.pricing_mode === PRICING_MODES.PERCENTAGE;
  const lines = [];

  if (snapshot.spend_cap != null) {
    const cap = Number(snapshot.spend_cap);
    if (Number.isFinite(cap)) {
      lines.push(`**Spend cap:** $${cap.toLocaleString("en-US")}`);
    }
  }

  const eventType = Array.isArray(snapshot.cpa_types) && snapshot.cpa_types[0];
  const fixedCpa = snapshot.fixed_cpa;
  if (eventType || fixedCpa != null) {
    let valueDisplay = "—";
    if (fixedCpa != null && Number.isFinite(Number(fixedCpa))) {
      valueDisplay = isPct ? `${Number((fixedCpa * 100).toFixed(4))}%` : `$${fixedCpa}`;
    }
    const label = eventType ? `${toTitleCase(eventType)}: ${valueDisplay}` : valueDisplay;
    lines.push(`**CPA:** ${label}`);
  }

  const geos = Array.isArray(snapshot.geo) ? snapshot.geo : [];
  if (geos.length) {
    const formatted = geos.map(g => {
      if (g && typeof g === "object") {
        const label = g.display_name || g.name || g.id || "Unknown";
        return g.clusivity === "exclude" ? `¬${label}` : label;
      }
      return String(g);
    });
    lines.push(`**Geos:** ${formatted.join(", ")}`);
  }

  const audio = Array.isArray(snapshot.audio_types) ? snapshot.audio_types : [];
  if (audio.length) {
    const formatted = audio.map(a => {
      if (a && typeof a === "object") {
        const label = a.display_name || a.name || a.value || "Unknown";
        return a.clusivity === "exclude" ? `¬${label}` : label;
      }
      return String(a);
    });
    lines.push(`**Audio Types:** ${formatted.join(", ")}`);
  }

  return lines.join("\n");
}

export function BudgetDrawer({ onClose, onSaved }) {
  const { effectiveAdvertiserId, isSupportUser, user, canManageAdvertiserData, canManageBilling } = useAuth();
  // Read on each render so test-time mutations of process.env take effect.
  const flagOn = process.env.REACT_APP_ENABLE_PERCENTAGE_PRICING === "true";
  const showModeToggle = isSupportUser && flagOn;
  const [pricingMode, setPricingMode] = useState(DEFAULT_PRICING_MODE);
  const [loading, setLoading]         = useState(true);
  const [saveError, setSaveError]     = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const hasExistingSettings = useRef(false);
  const pendingRowId = useRef(null);
  const [budget, setBudget]           = useState("");
  const [editingBudget, setEditingBudget] = useState(false);
  const [cpaType, setCpaType]         = useState([]);
  const [cpaTypeOpen, setCpaTypeOpen] = useState(false);
  const [availableCpaTypes, setAvailableCpaTypes] = useState([]);
  const [cpaTypesLoading, setCpaTypesLoading] = useState(false);
  const [cpa, setCpa]                 = useState("");
  const [editingCpa, setEditingCpa]   = useState(false);
  const [geoOpen, setGeoOpen]         = useState(false);
  const [geos, setGeos]               = useState([]);
  const [geoResults, setGeoResults]   = useState([]);
  const [geoAddAsExclude, setGeoAddAsExclude] = useState(false);
  const [geoTypeahead, setGeoTypeahead] = useState("");
  const geoInputRef = useRef(null);
  const [enabledGeoTypes, setEnabledGeoTypes] = useState([]);
  const [advertiserBudgetLimit, setAdvertiserBudgetLimit] = useState(null);
  const [geoTypesLoading, setGeoTypesLoading] = useState(false);
  const [geoTypesError, setGeoTypesError] = useState(null);
  const [activeGeoIndex, setActiveGeoIndex] = useState(-1);
  const [audioOpen, setAudioOpen]     = useState(false);
  const [audioTypes, setAudioTypes]   = useState([]);
  // Snapshot of the row at load time, used to compute the previous_state
  // markdown for notify-settings-changed. Survives across save retries; we
  // refresh it after a successful save so subsequent saves diff against the
  // freshly-persisted baseline rather than the original load.
  const originalSnapshotRef = useRef(null);

  const monthYear = getCurrentMonthFirstDay();

  // Fetch available CPA event types from Rill via Edge Function.
  // Pure fetcher: only populates `availableCpaTypes`. Reconciliation against
  // the loaded campaign_settings row happens in a dedicated effect below so
  // it works regardless of which async source resolves first.
  useEffect(() => {
    if (!supabase || effectiveAdvertiserId == null) return;
    let cancelled = false;
    setCpaTypesLoading(true);
    supabase.functions
      .invoke("rill-event-types", { body: { advertiser_id: effectiveAdvertiserId } })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (!error && Array.isArray(data?.event_types) && data.event_types.length > 0) {
          setAvailableCpaTypes(data.event_types);
        }
      })
      .finally(() => {
        if (cancelled) return;
        setCpaTypesLoading(false);
      });
    return () => { cancelled = true; };
  }, [effectiveAdvertiserId]);

  // Reconcile cpaType against Rill's casing once BOTH the row load and the
  // Rill fetch have settled. Dropping types not present in availableCpaTypes
  // is intentional: we cannot safely persist an event type Rill does not
  // recognize.
  useEffect(() => {
    if (loading || availableCpaTypes.length === 0) return;
    const rillByLower = new Map(availableCpaTypes.map(t => [t.toLowerCase(), t]));
    setCpaType(prev => {
      const next = prev
        .map(t => rillByLower.get(t.toLowerCase()) ?? t)
        .filter(t => rillByLower.has(t.toLowerCase()));
      return next.length === prev.length && next.every((t, i) => t === prev[i]) ? prev : next;
    });
  }, [loading, availableCpaTypes]);

  useEffect(() => {
    let ignore = false;
    setEnabledGeoTypes([]);
    setAdvertiserBudgetLimit(null);
    setGeoTypesError(null);
    setGeoTypesLoading(true);
    if (!supabase || effectiveAdvertiserId == null) { setGeoTypesLoading(false); return; }
    (async () => {
      const { data, error } = await supabase.from("advertisers").select("enabled_geo_types, budget_limit").eq("id", effectiveAdvertiserId).maybeSingle();
      if (ignore) return;
      if (error) setGeoTypesError(error.message);
      else {
        setEnabledGeoTypes(Array.isArray(data?.enabled_geo_types) ? data.enabled_geo_types : []);
        setAdvertiserBudgetLimit(data?.budget_limit != null ? Number(data.budget_limit) : null);
      }
      setGeoTypesLoading(false);
    })();
    return () => { ignore = true; };
  }, [effectiveAdvertiserId]);

  useEffect(() => {
    if (!supabase || effectiveAdvertiserId == null) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setSaveError(null);
      // Match DashboardScreen: hide rejected rows from the client. Pre-filling
      // the drawer with values the admin already turned down would be confusing
      // and would invite the same submission again. Falling through to the
      // previous approved/pending row gives the client a known-good baseline
      // to re-edit from. The save path below still INSERTs a fresh pending row
      // when status !== 'pending' (pendingRowId.current stays null).
      const { data: row, error } = await supabase
        .from("campaign_settings")
        .select("id, spend_cap, cpa_types, fixed_cpa, pricing_mode, geo, audio_types, status")
        .eq("advertiser_id", effectiveAdvertiserId)
        .eq("month_year", monthYear)
        .in("status", ["approved", "pending"])
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (!error && row) {
        hasExistingSettings.current = true;
        pendingRowId.current = row.status === "pending" ? row.id : null;
        originalSnapshotRef.current = {
          spend_cap: row.spend_cap,
          cpa_types: Array.isArray(row.cpa_types) ? row.cpa_types.slice(0, 1) : [],
          fixed_cpa: row.fixed_cpa,
          pricing_mode: row.pricing_mode === PRICING_MODES.PERCENTAGE ? PRICING_MODES.PERCENTAGE : PRICING_MODES.FIXED,
          geo: Array.isArray(row.geo) ? row.geo : [],
          audio_types: Array.isArray(row.audio_types) ? row.audio_types : [],
        };
        setBudget(row.spend_cap != null ? String(row.spend_cap) : "");
        // Take at most one event type. slice(0, 1) is defensive for legacy rows
        // that stored multiple types before the single-event restriction.
        const initialCpaType = Array.isArray(row.cpa_types) && row.cpa_types.length
          ? row.cpa_types.slice(0, 1)
          : [];
        setCpaType(initialCpaType);
        // Percentage mode: stored as decimal (0.1 = 10%) → display as whole number ("10").
        // toFixed(4) scrubs float artifacts (0.10 * 100 = 10.000...002).
        const loadedMode = row.pricing_mode === PRICING_MODES.PERCENTAGE ? PRICING_MODES.PERCENTAGE : PRICING_MODES.FIXED;
        const displayCpa = row.fixed_cpa != null
          ? (loadedMode === PRICING_MODES.PERCENTAGE
              ? String(Number((row.fixed_cpa * 100).toFixed(4)))
              : String(row.fixed_cpa))
          : "";
        setCpa(displayCpa);
        setPricingMode(loadedMode);
        const geoArr = Array.isArray(row.geo) ? row.geo : [];
        const parsed = geoArr.filter(g => g !== null && typeof g === "object");
        setGeos(parsed);
        const audioArr = Array.isArray(row.audio_types) ? row.audio_types.filter(a => a !== null && typeof a === "object") : [];
        setAudioTypes(audioArr);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [effectiveAdvertiserId, monthYear]);

  useEffect(() => {
    setActiveGeoIndex(-1);
    if (!geoTypeahead.trim()) { setGeoResults([]); return; }
    setGeoResults(searchGeos(geoTypeahead, ["state", "dma"]));
  }, [geoTypeahead]);

  const handleSave = async () => {
    if (!supabase || effectiveAdvertiserId == null || !canManageAdvertiserData || !canManageBilling) return;
    setSaveError(null);
    const parsedSpendCap = parseInt(budget, 10) || 20000;
    if (advertiserBudgetLimit != null && parsedSpendCap > advertiserBudgetLimit) {
      setSaveError(`Spend Cap cannot exceed advertiser budget limit of $${advertiserBudgetLimit.toLocaleString()}.`);
      setShowDisclaimer(false);
      return;
    }
    // Captured BEFORE this save writes anything: the resume-eligibility check below needs the
    // cap that was actually in effect (i.e. approved) when delivery may have been blocked, not
    // whatever the drawer loaded (originalSnapshotRef can be a newer, still-pending submission)
    // and not a cap this very save is about to approve. Only needed for support saves, which
    // are the only ones that can trigger a resume — see the isSupportUser check further down.
    let previousApprovedCap = null;
    let approvedCapLookupFailed = false;
    if (isSupportUser) {
      const { data: approvedRow, error: approvedCapError } = await supabase
        .from("campaign_settings")
        .select("spend_cap")
        .eq("advertiser_id", effectiveAdvertiserId)
        .eq("month_year", monthYear)
        .eq("status", "approved")
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (approvedCapError) {
        // Don't let a transient query error silently look like "no prior cap" — that would
        // skip the resume-eligibility check without anyone knowing it was skipped.
        approvedCapLookupFailed = true;
      } else {
        previousApprovedCap = approvedRow?.spend_cap ?? null;
      }
    }
    const geoToSaveFinal = geos.length ? geos : [DEFAULT_GEO];
    const payload = {
      advertiser_id: effectiveAdvertiserId,
      month_year: monthYear,
      spend_cap: parsedSpendCap,
      cpa_types: cpaType,
      fixed_cpa: (() => {
        const v = parseFloat(cpa);
        return pricingMode === PRICING_MODES.PERCENTAGE
          ? Number((v / 100).toFixed(6))
          : v;
      })(),
      geo: geoToSaveFinal,
      audio_types: audioTypes,
    };
    if (flagOn) {
      // Non-admin users have no toggle, so `pricingMode` stays at the loaded
      // value — passing it unchanged keeps the BEFORE UPDATE trigger a no-op.
      payload.pricing_mode = pricingMode;
    }
    // Admin saves auto-approve: an @audiohook.com user editing the row IS the
    // review, so we skip the pending queue entirely. Client saves omit these
    // fields, so the row keeps status='pending' (DB default for INSERT) or
    // stays pending (UPDATE) and surfaces in the admin review queue. The
    // enforce_campaign_settings_status_admin_only trigger (migration 027)
    // permits admins to write any status value; non-admins are blocked from
    // sending status, so the conditional spread is also a defensive guard.
    const adminApprovalFields = isSupportUser
      ? {
          status: "approved",
          reviewed_by: user?.email ?? null,
          reviewed_at: new Date().toISOString(),
        }
      : null;
    if (adminApprovalFields) {
      Object.assign(payload, adminApprovalFields);
    }
    if (pendingRowId.current) {
      const updatePayload = {
        spend_cap: payload.spend_cap,
        cpa_types: payload.cpa_types,
        fixed_cpa: payload.fixed_cpa,
        geo: payload.geo,
        audio_types: payload.audio_types,
        updated_at: new Date().toISOString(),
        ...(flagOn ? { pricing_mode: payload.pricing_mode } : {}),
        ...(adminApprovalFields ?? {}),
      };
      const { data: updated, error: updateErr } = await supabase
        .from("campaign_settings")
        .update(updatePayload)
        .eq("id", pendingRowId.current)
        .eq("status", "pending")
        .select("id")
        .maybeSingle();
      if (updateErr) {
        setSaveError(updateErr.message);
        return;
      }
      if (!updated) {
        // Row was approved while the drawer was open — fall back to INSERT.
        const { error: insertErr } = await supabase.from("campaign_settings").insert(payload);
        if (insertErr) {
          setSaveError(insertErr.message);
          return;
        }
      }
    } else {
      const { error: insertErr } = await supabase.from("campaign_settings").insert(payload);
      if (insertErr) {
        setSaveError(insertErr.message);
        return;
      }
    }
    // Fire-and-forget notification to the CSM. The Edge Function resolves the
    // CSM via Clarify and POSTs the event to Zapier (Zap owns delivery).
    // Skipped for admins because they're the ones who eventually approve/reject
    // the row and don't need to be paged about their own save. The Edge
    // Function additionally rejects @audiohook.com JWTs as defense-in-depth.
    // notifyChange swallows errors internally so a Clarify/Zapier outage never
    // blocks the drawer from closing.
    if (!isSupportUser) {
      const newSnapshot = {
        spend_cap: payload.spend_cap,
        cpa_types: payload.cpa_types,
        fixed_cpa: payload.fixed_cpa,
        pricing_mode: payload.pricing_mode ?? pricingMode,
        geo: payload.geo,
        audio_types: payload.audio_types,
      };
      notifyChange({
        supabase,
        eventType: "Dashboard - Campaign Settings",
        advertiserId: effectiveAdvertiserId,
        previousState: formatCampaignSettingsMarkdown(originalSnapshotRef.current),
        newState: formatCampaignSettingsMarkdown(newSnapshot),
      });
      // Refresh the baseline so a follow-up save in the same drawer session
      // diffs against what was just persisted, not the stale original load.
      originalSnapshotRef.current = newSnapshot;
    }
    // Only support-made budget increases can resume campaigns automatically. Self-service
    // saves land as a `pending` row awaiting admin review, so this must not run at all for
    // them — not even to check eligibility (that check runs again on approval instead, see
    // CampaignSettingsApprovalModal).
    let resumeMessage = null;
    if (isSupportUser) {
      resumeMessage = approvedCapLookupFailed
        ? RESUME_LOOKUP_FAILED_MESSAGE
        : await resumeCampaignsIfEligible({
            advertiserId: effectiveAdvertiserId,
            previousCap: previousApprovedCap,
            newCap: parsedSpendCap,
          });
    }
    onSaved?.(resumeMessage);
    onClose();
  };

  // Pricing-mode toggle handler: on a REAL mode change, clear the CPA value
  // so the user re-enters it in the new unit. A dollar amount like 30 is
  // meaningless as a percentage, and vice versa. No-op when the user clicks
  // the already-active mode.
  const handleModeChange = (newMode) => {
    if (newMode === pricingMode) return;
    setPricingMode(newMode);
    setCpa("");
  };

  const handleSaveClick = () => {
    setSaveError(null);
    if (cpaType.length === 0) {
      setSaveError("Select at least one CPA event type");
      return;
    }
    const cpaValue = parseFloat(cpa);
    if (!Number.isFinite(cpaValue) || cpaValue <= 0) {
      setSaveError("CPA value must be greater than 0");
      return;
    }
    if (pricingMode === PRICING_MODES.PERCENTAGE && cpaValue > 100) {
      setSaveError("CPA value cannot exceed 100%");
      return;
    }
    hasExistingSettings.current ? setShowDisclaimer(true) : handleSave();
  };

  const toggleAudio = (type) => {
    if (!canManageAdvertiserData || !canManageBilling) return;
    setAudioTypes(prev =>
      prev.some(t => t.value === type.value) ? prev.filter(t => t.value !== type.value) : [...prev, type]
    );
  };

  const audioLabel = audioTypes.length === BUDGET_DRAWER_ALL_AUDIO.length ? "All types" :
    audioTypes.length === 0 ? "None selected" : audioTypes.map(t => t.display_name).join(", ");

  const geoTrimmed = geoTypeahead.trim();
  const geoZipOpt = geoTrimmed && !geoTypesError && enabledGeoTypes.includes("zip_code") && validateZipCode(geoTrimmed)
    ? buildZipGeoObject(geoTrimmed, geoAddAsExclude ? "exclude" : "include")
    : null;

  const sectionDivider = { paddingBottom:16, marginBottom:16, borderBottom:"1px solid #E2E2E2" };
  const labelStyle = { fontSize:11, color:"#7b7b7b", textTransform:"uppercase", letterSpacing:1, marginBottom:8 };
  const editBtnStyle = { fontSize:12, color:"#F90091", background:"none", border:"none", cursor:"pointer" };
  const canEditCampaignSettings = canManageAdvertiserData && canManageBilling;
  const editBtnComputedStyle = {
    ...editBtnStyle,
    opacity: canEditCampaignSettings ? 1 : 0.5,
    cursor: canEditCampaignSettings ? "pointer" : "not-allowed",
  };
  const capCpaShortfall = pricingMode !== PRICING_MODES.PERCENTAGE ? capShortfall(budget, cpa) : null;

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.3)", display:"flex", justifyContent:"flex-end", zIndex:90 }} onClick={e => { if(e.target===e.currentTarget) onClose(); }}>
      <div style={{ background:"#FFFFFF", borderLeft:"1px solid #E2E2E2", width:420, height:"100%", display:"flex", flexDirection:"column", boxShadow:"-6px 0 28px rgba(0,0,0,0.12)" }}>
        <div style={{ flex:1, overflowY:"auto", padding:32, paddingBottom:16 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:28 }}>
            <h3 style={{ fontSize:22, fontWeight:600, color:"#151515", margin:0, fontFamily:"'Clash Display Variable','Clash Display',sans-serif" }}>Campaign Settings</h3>
            <button onClick={onClose} style={{ background:"none", border:"none", color:"#626262", fontSize:20, cursor:"pointer" }}>✕</button>
          </div>

          {loading && <div style={{ padding:24, textAlign:"center", color:"#7b7b7b" }}>Loading settings…</div>}
          {saveError && (
            <div style={{ padding:12, marginBottom:16, background:"#FFF5F5", border:"1px solid #fca5a5", borderRadius:10, color:"#e53935", fontSize:13 }}>{saveError}</div>
          )}
          {!canEditCampaignSettings && (
            <div style={{ padding:12, marginBottom:16, background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:10, color:"#626262", fontSize:12 }}>
              Read-only access: only billing-enabled admins/members can update campaign settings.
            </div>
          )}

          {!loading && (
          <>
          <div style={{ background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:14, padding:20, marginBottom:20 }}>

            <div style={sectionDivider}>
              <div style={labelStyle}>Spend Cap</div>
              {advertiserBudgetLimit != null && (
                <div style={{ marginBottom:8, fontSize:12, color:"#626262" }}>
                  Monthly Credit Limit: <span style={{ color:"#151515", fontWeight:600 }}>${advertiserBudgetLimit.toLocaleString()}</span>
                </div>
              )}
              {editingBudget ? (
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  <div style={{ display:"flex", alignItems:"center", background:"#FFFFFF", border:"1px solid #F90091", borderRadius:8, padding:"6px 12px", flex:1 }}>
                    <span style={{ color:"#7b7b7b", fontSize:13 }}>$</span>
                    <input autoFocus value={budget} onChange={e => {
                      const raw = e.target.value.replace(/[^0-9]/g, "");
                      if (!raw) { setBudget(""); return; }
                      if (advertiserBudgetLimit != null) {
                        setBudget(String(Math.min(parseInt(raw, 10), advertiserBudgetLimit)));
                        return;
                      }
                      setBudget(raw);
                    }}
                      placeholder="20000"
                      style={{ background:"transparent", border:"none", outline:"none", color:"#151515", fontSize:15, fontWeight:600, width:"100%", marginLeft:4 }} />
                  </div>
                  <button onClick={() => setEditingBudget(false)} style={{ padding:"6px 14px", background:"#F90091", color:"#fff", border:"none", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer" }}>Done</button>
                </div>
              ) : (
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span style={{ color:"#151515", fontWeight:600, fontSize:16 }}>${Number(budget).toLocaleString()}</span>
                  <button onClick={() => canEditCampaignSettings && setEditingBudget(true)} disabled={!canEditCampaignSettings} aria-label="Edit Spend Cap" style={editBtnComputedStyle}>Edit</button>
                </div>
              )}
              <ShortfallWarning shortfall={capCpaShortfall} noun="cap" />
            </div>

            <div style={sectionDivider}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                <div style={labelStyle}>CPA Type</div>
                <button onClick={() => canEditCampaignSettings && setCpaTypeOpen(!cpaTypeOpen)} disabled={!canEditCampaignSettings} aria-label="Edit CPA Types" style={editBtnComputedStyle}>Edit</button>
              </div>
              <div style={{ fontSize:13, color:"#151515", fontWeight:500, marginBottom: cpaTypeOpen ? 10 : 0 }}>
                {cpaTypesLoading
                  ? <span style={{ color:"#B8B8B8" }}>Loading…</span>
                  : cpaType.length === availableCpaTypes.length && availableCpaTypes.length > 1
                    ? "All types"
                    : cpaType.length === 0 ? "None selected"
                    : cpaType.map(toTitleCase).join(", ")}
              </div>
              {cpaTypeOpen && (
                <div style={{ display:"flex", flexDirection:"column", gap:8, marginTop:8 }}>
                  {cpaTypesLoading && <div style={{ fontSize:12, color:"#B8B8B8", padding:"8px 0" }}>Loading event types…</div>}
                  {!cpaTypesLoading && availableCpaTypes.length === 0 && <div style={{ fontSize:12, color:"#B8B8B8", padding:"8px 0" }}>No event types available</div>}
                  {availableCpaTypes.map(opt => {
                    const selected = cpaType.includes(opt);
                    return (
                      <div key={opt} onClick={() => setCpaType(selected ? [] : [opt])}
                        style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", background:"#FFFFFF", borderRadius:8, cursor:"pointer", border:`1px solid ${selected ? "#F90091" : "#E2E2E2"}` }}>
                        <div style={{ width:16, height:16, borderRadius:"50%", background: selected ? "#F90091" : "#F0F0F0", border:`1px solid ${selected ? "#F90091" : "#CFCFCF"}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          {selected && <div style={{ width:6, height:6, borderRadius:"50%", background:"#fff" }} />}
                        </div>
                        <span style={{ fontSize:13, color: selected ? "#151515" : "#626262" }}>{toTitleCase(opt)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {showModeToggle && (
              <div style={sectionDivider}>
                <div style={labelStyle}>Pricing Mode</div>
                <div role="group" aria-label="Pricing mode" style={{ display:"flex", gap:6 }}>
                  <button
                    type="button"
                    onClick={() => handleModeChange(PRICING_MODES.FIXED)}
                    style={{ padding:"6px 14px", borderRadius:8, fontSize:12, fontWeight:600, border:`1px solid ${pricingMode === PRICING_MODES.FIXED ? "#F90091" : "#E2E2E2"}`, background: pricingMode === PRICING_MODES.FIXED ? "#FFE9F6" : "#FFFFFF", color: pricingMode === PRICING_MODES.FIXED ? "#F90091" : "#626262", cursor:"pointer" }}
                  >Fixed</button>
                  <button
                    type="button"
                    onClick={() => handleModeChange(PRICING_MODES.PERCENTAGE)}
                    style={{ padding:"6px 14px", borderRadius:8, fontSize:12, fontWeight:600, border:`1px solid ${pricingMode === PRICING_MODES.PERCENTAGE ? "#F90091" : "#E2E2E2"}`, background: pricingMode === PRICING_MODES.PERCENTAGE ? "#FFE9F6" : "#FFFFFF", color: pricingMode === PRICING_MODES.PERCENTAGE ? "#F90091" : "#626262", cursor:"pointer" }}
                  >Percentage</button>
                </div>
              </div>
            )}

            <div style={sectionDivider}>
              <div style={labelStyle}>{pricingMode === PRICING_MODES.PERCENTAGE ? "CPA Value" : "Fixed CPA"}</div>
              {editingCpa ? (
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  <div style={{ display:"flex", alignItems:"center", background:"#FFFFFF", border:"1px solid #F90091", borderRadius:8, padding:"6px 12px", flex:1 }}>
                    {pricingMode !== PRICING_MODES.PERCENTAGE && <span style={{ color:"#7b7b7b", fontSize:13 }}>$</span>}
                    <input autoFocus value={cpa} onChange={e => {
                        setCpa(sanitizeCpaInput(e.target.value));
                      }}
                      placeholder={pricingMode === PRICING_MODES.PERCENTAGE ? "5.00" : "45.00"}
                      style={{ background:"transparent", border:"none", outline:"none", color:"#151515", fontSize:15, fontWeight:600, width:"100%", marginLeft: pricingMode !== PRICING_MODES.PERCENTAGE ? 4 : 0 }} />
                    {pricingMode === PRICING_MODES.PERCENTAGE && <span style={{ color:"#7b7b7b", fontSize:13, marginLeft:4 }}>%</span>}
                  </div>
                  <button onClick={() => setEditingCpa(false)} style={{ padding:"6px 14px", background:"#F90091", color:"#fff", border:"none", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer" }}>Done</button>
                </div>
              ) : (
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span style={{ color:"#151515", fontWeight:600, fontSize:16 }}>
                    {pricingMode === PRICING_MODES.PERCENTAGE
                      ? `${parseFloat(cpa || 0).toLocaleString("en-US", { minimumFractionDigits:2, maximumFractionDigits:2 })}%`
                      : `$${parseFloat(cpa || 0).toLocaleString("en-US", { minimumFractionDigits:2, maximumFractionDigits:2 })}`}
                  </span>
                  <button onClick={() => canEditCampaignSettings && setEditingCpa(true)} disabled={!canEditCampaignSettings} aria-label="Edit CPA Value" style={editBtnComputedStyle}>Edit</button>
                </div>
              )}
            </div>

            <div style={{ ...sectionDivider, position:"relative" }}>
              <div style={labelStyle}>
                Geos (States & DMAs{!geoTypesLoading && !geoTypesError && enabledGeoTypes.includes("zip_code") ? " & Zip Codes" : ""})
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                <span style={{ color:"#151515", fontSize:13, fontWeight:500 }}>Include or exclude geos</span>
                <button onClick={() => canEditCampaignSettings && setGeoOpen(!geoOpen)} disabled={!canEditCampaignSettings} aria-label={geoOpen ? "Done editing Geos" : "Edit Geos"} style={editBtnComputedStyle}>{geoOpen ? "Done" : "Edit"}</button>
              </div>
              {geoTypesError && (
                <div style={{ padding:"8px 10px", marginBottom:8, background:"#FFF5F5", border:"1px solid #fca5a5", borderRadius:8, color:"#e53935", fontSize:12 }}>
                  Could not load feature flags: {geoTypesError}
                </div>
              )}
              {geoOpen && (
                <>
                  {geos.filter(g => g.clusivity === "include").length > 0 && (
                    <div style={{ marginBottom:10 }}>
                      <div style={{ fontSize:10, color:"#7b7b7b", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>Included</div>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                        {geos.filter(g => g.clusivity === "include").map(geo => (
                          <span key={geo.id} style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"4px 8px", background:"#EEF2FF", color:"#323084", borderRadius:6, fontSize:12 }}>
                            {geo.display_name || geo.name}
                            <button type="button" onClick={() => setGeos(prev => prev.filter(g => g.id !== geo.id))} style={{ background:"none", border:"none", color:"#323084", cursor:"pointer", padding:0, lineHeight:1, fontSize:14 }} aria-label="Remove">×</button>
                            <button type="button" onClick={() => setGeos(prev => prev.map(g => g.id === geo.id ? { ...g, clusivity:"exclude" } : g))} style={{ background:"none", border:"none", color:"#7b7b7b", cursor:"pointer", padding:0, fontSize:10 }} title="Exclude">exclude</button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {geos.filter(g => g.clusivity === "exclude").length > 0 && (
                    <div style={{ marginBottom:10 }}>
                      <div style={{ fontSize:10, color:"#7b7b7b", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>Excluded</div>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                        {geos.filter(g => g.clusivity === "exclude").map(geo => (
                          <span key={geo.id} style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"4px 8px", background:"#FFF5F5", color:"#e53935", borderRadius:6, fontSize:12 }}>
                            {geo.display_name || geo.name}
                            <button type="button" onClick={() => setGeos(prev => prev.filter(g => g.id !== geo.id))} style={{ background:"none", border:"none", color:"#e53935", cursor:"pointer", padding:0, lineHeight:1, fontSize:14 }} aria-label="Remove">×</button>
                            <button type="button" onClick={() => setGeos(prev => prev.map(g => g.id === geo.id ? { ...g, clusivity:"include" } : g))} style={{ background:"none", border:"none", color:"#7b7b7b", cursor:"pointer", padding:0, fontSize:10 }} title="Include">include</button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:6 }}>
                    <span style={{ fontSize:11, color:"#7b7b7b" }}>Add as:</span>
                    <button type="button" onClick={() => setGeoAddAsExclude(false)} style={{ padding:"4px 10px", borderRadius:6, fontSize:12, border:"1px solid #E2E2E2", background: !geoAddAsExclude ? "#EEF2FF" : "transparent", color: !geoAddAsExclude ? "#323084" : "#626262", cursor:"pointer" }}>Include</button>
                    <button type="button" onClick={() => setGeoAddAsExclude(true)} style={{ padding:"4px 10px", borderRadius:6, fontSize:12, border:"1px solid #E2E2E2", background: geoAddAsExclude ? "#FFF5F5" : "transparent", color: geoAddAsExclude ? "#e53935" : "#626262", cursor:"pointer" }}>Exclude</button>
                  </div>
                  <div style={{ position:"relative", marginBottom:8 }}>
                    <input
                      ref={geoInputRef}
                      type="text"
                      value={geoTypeahead}
                      onChange={e => setGeoTypeahead(e.target.value)}
                      onKeyDown={e => {
                        const totalOptions = (geoZipOpt ? 1 : 0) + geoResults.length;
                        if (e.key === "ArrowDown") { e.preventDefault(); setActiveGeoIndex(i => Math.min(i+1, totalOptions-1)); return; }
                        if (e.key === "ArrowUp") { e.preventDefault(); setActiveGeoIndex(i => Math.max(i-1, -1)); return; }
                        if (e.key === "Escape") { setGeoTypeahead(""); setActiveGeoIndex(-1); return; }
                        if (e.key !== "Enter") return;
                        e.preventDefault();
                        if (activeGeoIndex >= 0) {
                          const isZipRow = geoZipOpt && activeGeoIndex === 0;
                          if (isZipRow) { if (!geos.some(g => g.id === geoZipOpt.id)) setGeos(prev => [...prev, geoZipOpt]); }
                          else {
                            const resultIdx = geoZipOpt ? activeGeoIndex - 1 : activeGeoIndex;
                            const opt = geoResults[resultIdx];
                            if (opt && !geos.some(g => g.id === opt.id)) setGeos(prev => [...prev, { ...opt, clusivity: geoAddAsExclude ? "exclude" : "include" }]);
                          }
                          setGeoTypeahead(""); setActiveGeoIndex(-1); return;
                        }
                        if (!geoZipOpt || geos.some(g => g.id === geoZipOpt.id)) return;
                        setGeos(prev => [...prev, geoZipOpt]);
                        setGeoTypeahead("");
                      }}
                      role="combobox"
                      aria-autocomplete="list"
                      aria-expanded={geoTrimmed !== ""}
                      aria-controls={geoTrimmed ? "geo-listbox" : undefined}
                      aria-activedescendant={activeGeoIndex >= 0 ? `geo-option-${activeGeoIndex}` : undefined}
                      placeholder={!geoTypesError && enabledGeoTypes.includes("zip_code") ? "Search states, DMAs or enter zip code…" : "Type to search states or DMAs…"}
                      style={{ width:"100%", boxSizing:"border-box", padding:"10px 12px", background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:8, color:"#151515", fontSize:13, outline:"none" }}
                    />
                    {geoTrimmed !== "" && (() => {
                      const hasResults = geoResults.length > 0 || geoZipOpt;
                      return (
                        <div id="geo-listbox" role="listbox" style={{ position:"absolute", top:"100%", left:0, right:0, background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:8, zIndex:20, maxHeight:220, overflowY:"auto", marginTop:4, boxShadow:"0 4px 12px rgba(0,0,0,0.10)" }}>
                          {geoZipOpt && (() => {
                            const already = geos.some(g => g.id === geoZipOpt.id);
                            const isActive = activeGeoIndex === 0;
                            return (
                              <button
                                type="button" id="geo-option-0" role="option" aria-selected={isActive}
                                onClick={() => { if (!already) setGeos(prev => [...prev, geoZipOpt]); setGeoTypeahead(""); setActiveGeoIndex(-1); geoInputRef.current?.focus(); }}
                                style={{ display:"block", width:"100%", textAlign:"left", padding:"10px 12px", fontSize:13, color: already ? "#B8B8B8" : "#151515", cursor: already ? "default" : "pointer", background: isActive ? "#FFF5FB" : already ? "#FAFAFA" : "transparent", border:"none", borderBottom: geoResults.length ? "1px solid #F0F0F0" : "none" }}
                              >
                                {geoTrimmed} <span style={{ fontSize:11, color:"#7b7b7b" }}>(Zip Code)</span>
                                {already && <span style={{ marginLeft:6, fontSize:10, color:"#F90091" }}>Already added</span>}
                              </button>
                            );
                          })()}
                          {geoResults.map((opt, i) => {
                            const flatIdx = geoZipOpt ? i + 1 : i;
                            const alreadySelected = geos.some(g => g.id === opt.id);
                            const existing = geos.find(g => g.id === opt.id);
                            const isActive = activeGeoIndex === flatIdx;
                            return (
                              <button
                                type="button" key={opt.id} id={`geo-option-${flatIdx}`} role="option" aria-selected={isActive}
                                onClick={() => { if (!alreadySelected) setGeos(prev => [...prev, { ...opt, clusivity: geoAddAsExclude ? "exclude" : "include" }]); setGeoTypeahead(""); setActiveGeoIndex(-1); geoInputRef.current?.focus(); }}
                                style={{ display:"block", width:"100%", textAlign:"left", padding:"10px 12px", fontSize:13, color: alreadySelected ? "#B8B8B8" : "#151515", cursor: alreadySelected ? "default" : "pointer", background: isActive ? "#FFF5FB" : alreadySelected ? "#FAFAFA" : "transparent", border:"none" }}
                              >
                                {opt.display_name || opt.name} <span style={{ fontSize:11, color:"#7b7b7b" }}>({opt.type === "state" ? "State" : "DMA"})</span>
                                {existing?.clusivity === "include" && <span style={{ marginLeft:6, fontSize:10, color:"#323084" }}>Included</span>}
                                {existing?.clusivity === "exclude" && <span style={{ marginLeft:6, fontSize:10, color:"#e53935" }}>Excluded</span>}
                              </button>
                            );
                          })}
                          {!hasResults && <div style={{ padding:12, color:"#7b7b7b", fontSize:13 }}>No matches</div>}
                        </div>
                      );
                    })()}
                  </div>
                  <div style={{ marginTop:8 }}>
                    <label style={{ display:"inline-block", padding:"8px 14px", background:"#F0F0F0", color:"#626262", borderRadius:8, fontSize:12, cursor:"pointer" }}>
                      Upload CSV{!geoTypesError && enabledGeoTypes.includes("zip_code") ? " (states, DMAs or zip codes)" : " (states or DMAs)"}
                      <input type="file" accept=".csv,.txt" style={{ display:"none" }} onChange={e => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = () => {
                          const text = reader.result;
                          const stateCodes = parseGeoCSV(text);
                          const stateGeos = fetchGeosByKeys(stateCodes.map(c => c.replace("US-", "")), ["state","dma"]).map(g => ({ ...g, clusivity:"include" }));
                          const zipGeos = !geoTypesError && enabledGeoTypes.includes("zip_code") ? parseZipCSV(text).map(z => buildZipGeoObject(z, "include")) : [];
                          const incoming = [...stateGeos, ...zipGeos];
                          setGeos(prev => { const existingIds = new Set(prev.map(g => g.id)); return [...prev, ...incoming.filter(g => !existingIds.has(g.id))]; });
                        };
                        reader.readAsText(file);
                        e.target.value = "";
                      }} />
                    </label>
                  </div>
                </>
              )}
              {!geoOpen && geos.length > 0 && (
                <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:4 }}>
                  {geos.filter(g => g.clusivity === "include").slice(0, 3).map(geo => (
                    <span key={geo.id} style={{ padding:"2px 6px", background:"#EEF2FF", color:"#323084", borderRadius:4, fontSize:12 }}>{geo.display_name || geo.name}</span>
                  ))}
                  {geos.filter(g => g.clusivity === "exclude").slice(0, 2).map(geo => (
                    <span key={geo.id} style={{ padding:"2px 6px", background:"#FFF5F5", color:"#e53935", borderRadius:4, fontSize:12 }}>¬{geo.display_name || geo.name}</span>
                  ))}
                  {geos.length > 5 && <span style={{ color:"#7b7b7b", fontSize:12 }}>+{geos.length - 5} more</span>}
                </div>
              )}
            </div>

            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                <div style={labelStyle}>Audio Types</div>
                <button onClick={() => canEditCampaignSettings && setAudioOpen(!audioOpen)} disabled={!canEditCampaignSettings} aria-label="Edit Audio Types" style={editBtnComputedStyle}>Edit</button>
              </div>
              <div style={{ fontSize:13, color:"#151515", fontWeight:500, marginBottom: audioOpen ? 10 : 0 }}>{audioLabel}</div>
              {audioOpen && (
                <div style={{ display:"flex", flexDirection:"column", gap:8, marginTop:8 }}>
                  {BUDGET_DRAWER_ALL_AUDIO.map(type => {
                    const selected = audioTypes.some(t => t.value === type.value);
                    return (
                      <div key={type.value} onClick={() => toggleAudio(type)}
                        style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", background:"#FFFFFF", borderRadius:8, cursor:"pointer", border:`1px solid ${selected ? "#F90091" : "#E2E2E2"}` }}>
                        <div style={{ width:16, height:16, borderRadius:4, background: selected ? "#F90091" : "#F0F0F0", border:`1px solid ${selected ? "#F90091" : "#CFCFCF"}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          {selected && <span style={{ color:"#fff", fontSize:10, lineHeight:1 }}>✓</span>}
                        </div>
                        <span style={{ fontSize:13, color: selected ? "#151515" : "#626262" }}>{type.display_name}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          </>
          )}
        </div>
        {!loading && (
          <div style={{ padding:"16px 32px 28px", borderTop:"1px solid #E2E2E2", background:"#FFFFFF" }}>
            <button onClick={() => canEditCampaignSettings && handleSaveClick()} disabled={!canEditCampaignSettings} style={{ width:"100%", padding:"13px 0", background:"#F90091", color:"#fff", border:"none", borderRadius:10, fontSize:14, fontWeight:600, cursor: canEditCampaignSettings ? "pointer" : "not-allowed", opacity: canEditCampaignSettings ? 1 : 0.55 }}>Save</button>
          </div>
        )}
      </div>

      {showDisclaimer && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.3)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:100 }}>
          <div style={{ background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:16, padding:28, maxWidth:420, width:"90%", boxShadow:"0 8px 32px rgba(0,0,0,0.15)" }}>
            <h4 style={{ fontSize:16, fontWeight:600, color:"#151515", margin:"0 0 12px 0", fontFamily:"'Clash Display Variable','Clash Display',sans-serif" }}>Confirm Campaign Changes</h4>
            <p style={{ fontSize:13, color:"#626262", lineHeight:1.6, margin:"0 0 20px 0" }}>
              These settings will apply to your campaign going forward. Please note that attributed
              conversions may continue to be reported for several more days based on impressions
              already served — this is expected and reflects your attribution window.
            </p>
            {saveError && (
              <div style={{ padding:"10px 12px", marginBottom:16, background:"#FFF5F5", border:"1px solid #fca5a5", borderRadius:8, color:"#e53935", fontSize:13 }}>{saveError}</div>
            )}
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button onClick={() => { setShowDisclaimer(false); setSaveError(null); }} style={{ padding:"9px 20px", background:"#FAFAFA", color:"#626262", border:"1px solid #E2E2E2", borderRadius:8, fontSize:13, fontWeight:500, cursor:"pointer" }}>Cancel</button>
              <button onClick={handleSave} style={{ padding:"9px 20px", background:"#F90091", color:"#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:600, cursor:"pointer" }}>Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
